import Markdown, { Components } from "react-markdown";
import { Info } from "lucide-react";
import React, { createContext, ReactElement, ReactNode, useContext } from "react";
import remarkDirective from "remark-directive";
import remarkDirectiveRehype from "remark-directive-rehype";
import remarkGfm from "remark-gfm";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../../ui/table";
import { cn } from "../../../lib/utils";
import { ArrowMarkdownComponent } from "./components/arrow";
import { ProjectWorkstreamIcon } from "../project/workstream-header";
import { DeliverableIcon } from "../project/deliverable-card";
import { useNavigate } from "react-router-dom";
import { useProject } from "../../../data/project";

const customMarkdownComponents: Record<string, Components["p"]> = {
    arrow(props) {
        return <ArrowMarkdownComponent {...props} />;
    },
    numbered(props) {
        return (
            <div className={"my-1 flex flex-row items-start gap-2"}>
                <div
                    className={
                        "mr-1 inline-block h-6 w-6 shrink-0 rounded-xl bg-accent text-center text-sm font-semibold leading-6 text-muted-foreground"
                    }
                >
                    {props.id}
                </div>
                <div>{props.children}</div>
            </div>
        );
    },
    info(props) {
        return (
            <div className={"flex flex-row items-start gap-1 space-x-1 text-sm text-muted-foreground"}>
                <Info className={"-mt-0.5 shrink-0 p-1"} />
                <div>{props.children}</div>
            </div>
        );
    },
    infobox(props) {
        return (
            <div
                className={
                    "flex flex-row items-start gap-1 space-x-1 rounded-md border p-3 text-sm text-muted-foreground"
                }
            >
                <Info className={"-mt-0.5 shrink-0 p-1"} />
                <div>{props.children}</div>
            </div>
        );
    },
    br() {
        return <br />;
    },
    workstream: function Workstream(props) {
        const navigate = useNavigate();
        const { project } = useProject();
        const wsIndex = project.outline.workstreams.findIndex((w) => w.id === props.id);
        return (
            <div
                className={"inline-flex cursor-pointer items-center italic transition-all hover:scale-[99%]"}
                onClick={() => props.id && navigate(`?wsId=${props.id}`)}
            >
                {props.id && (
                    <ProjectWorkstreamIcon
                        ws={{ id: props.id }}
                        index={wsIndex >= 0 ? wsIndex : undefined}
                        size={"xs"}
                        inline
                        className={"me-1"}
                    />
                )}
                {props.children}
            </div>
        );
    },
    deliverable: function Deliverable(props) {
        const navigate = useNavigate();
        const { project } = useProject();
        const [workstreamId, deliverableId] = props.id ? props.id.split("::") : [undefined, undefined];
        const wsIndex = project.outline.workstreams.findIndex((w) => w.id === workstreamId);
        const ws = project.outline.workstreams[wsIndex];
        const dIndex = ws?.deliverables.findIndex((d) => d.id === deliverableId) ?? -1;
        return (
            <div
                className={"inline-flex cursor-pointer items-center italic transition-all hover:scale-[99%]"}
                onClick={() => workstreamId && deliverableId && navigate(`?wsId=${workstreamId}&dId=${deliverableId}`)}
            >
                {workstreamId && deliverableId && (
                    <DeliverableIcon
                        deliverableId={deliverableId}
                        workstreamIndex={wsIndex >= 0 ? wsIndex : undefined}
                        deliverableIndex={dIndex >= 0 ? dIndex : undefined}
                        inline
                        size={"xs"}
                        className={"me-1"}
                    />
                )}
                {props.children}
            </div>
        );
    },
    tekkr_action_placeholder(props) {
        return <ActionPlaceholderComponent placeholderId={props.id!} />;
    },
};

function ActionPlaceholderComponent({ placeholderId }: { placeholderId: string }) {
    const ctx = useContext(ActionPlaceholderContext);
    if (!ctx) return <>no ctx</>;
    return <>{ctx(placeholderId)}</>;
}

const markdownComponents: Components = {
    h1(props) {
        return <h1 className={"mt-4"}>{props.children}</h1>;
    },
    h2(props) {
        return <h2 className={"mt-4"}>{props.children}</h2>;
    },
    h3(props) {
        return <h3 className={"mt-3"}>{props.children}</h3>;
    },
    h4(props) {
        return <h4>{props.children}</h4>;
    },
    table(props) {
        if (!props.children) {
            return <></>;
        }

        let width = 100;
        const children = props.children as ReactElement[];
        const thead = children[0];
        const headerCells = thead?.props?.children?.props?.children;
        if (Array.isArray(headerCells)) {
            width = headerCells.length <= 4 ? 100 : 30 * headerCells.length;
        }

        return (
            <div className={"overflow-x-scroll"}>
                <div style={{ width: `${width}%` }}>
                    <Table>{props.children}</Table>
                </div>
            </div>
        );
    },
    thead(props) {
        return <TableHeader>{props.children}</TableHeader>;
    },
    tbody(props) {
        return <TableBody>{props.children}</TableBody>;
    },
    tr(props) {
        return <TableRow>{props.children}</TableRow>;
    },
    td(props) {
        return <TableCell>{props.children}</TableCell>;
    },
    a(props) {
        return <a className={"underline"} target={"_blank"} rel={"noreferrer"} {...props}></a>;
    },
    p(props) {
        const isSimpleArrowParagraph = typeof props.children === "string" && props.children.startsWith("->");
        const isComplexArrowParagraph =
            props.children &&
            typeof props.children === "object" &&
            "0" in props.children &&
            typeof props.children[0] === "string" &&
            props.children[0].startsWith("->");
        if (isSimpleArrowParagraph || isComplexArrowParagraph) {
            let children = props.children;
            if (isSimpleArrowParagraph) {
                children = (children as string).slice(2);
            } else if (isComplexArrowParagraph) {
                children = [((children as ReactNode[])[0] as string).slice(2), ...(children as ReactNode[]).slice(1)];
            }
            return <ArrowMarkdownComponent {...props}>{children}</ArrowMarkdownComponent>;
        }
        return <p {...props}>{props.children}</p>;
    },
    blockquote(props) {
        return <blockquote className={"border-0 border-l-4 border-secondary pl-2 italic"}>{props.children}</blockquote>;
    },
    pre(props) {
        return (
            <div className={"overflow-x-scroll rounded bg-accent px-2 py-2 text-secondary-foreground"}>
                <div className={"whitespace-pre-wrap font-mono text-sm"}>{props.children}</div>
            </div>
        );
    },
    code(props) {
        return <code className={"rounded bg-accent px-1 py-0.5 font-mono text-sm"}>{props.children}</code>;
    },
};

const allMarkdownComponents = {
    ...markdownComponents,
    ...customMarkdownComponents,
};

export const ActionPlaceholderContext = createContext<((id: string) => ReactNode) | null>(null);

export function TekkrMarkdown(props: { markdown: string; className?: string }) {
    return (
        <div className={cn("flex w-full flex-col gap-2", props.className)}>
            <Markdown
                remarkPlugins={[remarkDirective, remarkDirectiveRehype, remarkGfm]}
                components={allMarkdownComponents}
            >
                {props.markdown}
            </Markdown>
        </div>
    );
}
